"use client";
import { User, useAuthContext } from "@/contexts/common/AuthContextProvider";
import * as Ably from "ably";
import { LiveObjects } from "ably/liveobjects";
import { AblyProvider as AProvider } from "ably/react";

import { useEffect, type JSX } from "react";
import { canUseAbly } from "@/utils/common/region";

export default function AblyProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>): JSX.Element {
  const { user } = useAuthContext();
  const ablyEnabled = canUseAbly();
  const client = ablyEnabled && user ? GetAblyClient(user) : null;

  useEffect(() => {
    if (client?.connection.state === "initialized") {
      client.connect();
    }
  }, [client]);

  if (!client) {
    return <>{children}</>;
  }

  return <AProvider client={client}>{children}</AProvider>;
}

let globalClient: Ably.Realtime | null = null;

const GetAblyClient = (user: User) => {
  if (globalClient) {
    return globalClient;
  }

  const isBrowser = globalThis.location !== undefined;
  const clientId = encodeURIComponent(user?.id || "genericUser");

  globalClient = new Ably.Realtime({
    authCallback: isBrowser
      ? async (_data, callback) => {
          try {
            const res = await fetch(`${globalThis.location.origin}/api/ably/${clientId}`, {
              method: "POST",
            });
            if (!res.ok) {
              callback(`Ably auth failed: ${res.status}`, null);
              return;
            }
            callback(null, await res.json());
          } catch (err) {
            callback(String(err), null);
          }
        }
      : (_data, callback) => {
          callback("SSR - no auth", null);
        },
    autoConnect: false,
    recover: (_, cb) => {
      cb(true);
    },
    plugins: { LiveObjects },
  });

  return globalClient;
};
